(() => {
'use strict';

const ID_MAP = {
  "plans": "[data-spc-plans]",
  "addPlan": "[data-spc-add-plan]",
  "days": "[data-spc-days]",
  "buffer": "[data-spc-buffer]",
  "rawWeights": "[data-spc-purchase-weights]",
  "costOn": "[data-spc-cost-toggle]",
  "curWrap": "[data-spc-currency-wrap]",
  "currency": "[data-spc-currency]",
  "calcBtn": "[data-spc-calculate]",
  "results": "[data-spc-results]",
  "shopSummary": "[data-spc-shopping-summary]",
  "kpis": "[data-spc-kpis]",
  "shopHead": "[data-spc-shopping-head]",
  "shopTable": ".shopping-prep-calculator__table",
  "shopLegend": "[data-spc-shopping-legend]",
  "copyBtn": "[data-spc-copy]",
  "printBtn": "[data-spc-print]",
  "suppCard": "[data-spc-supplement-card]",
  "suppList": "[data-spc-supplement-list]",
  "prepHint": "[data-spc-prep-hint]",
  "prepPlans": "[data-spc-prep-plans]",
  "prepNotes": "[data-spc-prep-notes]",
  "totCost": "[data-spc-total-cost]"
};
const EXACT_SELECTORS = {
  "#shopTable tbody": "[data-spc-shopping-table]",
  "#shopTable": ".shopping-prep-calculator__table",
  ".daysChip": "[data-spc-days-preset]",
  "#kpis .kpi": "[data-spc-kpis] .shopping-prep-calculator__kpi"
};

class SShoppingPrepCalculator extends HTMLElement {
  connectedCallback() {
    if (this.__spcInitialized) return;
    this.__spcInitialized = true;

    const root = this;
    const nativeDocument = window.document;
    const document = {
      getElementById(id) {
        const selector = ID_MAP[id];
        return selector ? root.querySelector(selector) : null;
      },
      querySelector(selector) {
        const mapped = EXACT_SELECTORS[selector] || selector;
        return root.querySelector(mapped);
      },
      querySelectorAll(selector) {
        const mapped = EXACT_SELECTORS[selector] || selector;
        return root.querySelectorAll(mapped);
      },
      createElement: nativeDocument.createElement.bind(nativeDocument),
      body: nativeDocument.body
    };

    const configNode = root.querySelector('script[type="application/json"][data-spc-config]');
    let SPC_CONFIG = { labels: {}, validation: {}, links: {} };
    if(configNode){
      try {
        SPC_CONFIG = JSON.parse(configNode.textContent || '{}');
      } catch(error) {
        console.error('Shopping Prep Calculator: invalid configuration JSON.', error);
      }
    }

    /* ================= INGREDIENT DATABASE =================
       n = display name (matches the Homemade Food Calculator exactly, so pasted
       recipes map automatically). ck = cooked-recipe purchase factor (grams to BUY
       per gram FED, cooked recipes lose water in the pan). rw = raw-recipe factor.
       unitG/unitName = natural buying unit. oilML = show as ml.               */
    const CATS = {
      meat:"Meat, fish & eggs", organ:"Organ meats", carb:"Carbs & grains",
      veg:"Vegetables", fruit:"Fruit", topper:"Dairy & toppers", oil:"Oils",
      mine:"Your own foods", units:"Your own foods — packs & units"
    };
    const MEAT_NOTE = "buy raw — meat loses ~27% water when cooked";
    const DB = {
      chicken_breast:{cat:"meat",n:"Chicken breast (no skin)",ck:1.37,rw:1,note:MEAT_NOTE},
      chicken_thigh:{cat:"meat",n:"Chicken thigh (no skin)",ck:1.37,rw:1,note:MEAT_NOTE},
      turkey:{cat:"meat",n:"Turkey (no skin)",ck:1.37,rw:1,note:MEAT_NOTE},
      duck:{cat:"meat",n:"Duck (no skin)",ck:1.37,rw:1,note:MEAT_NOTE},
      beef_lean:{cat:"meat",n:"Beef, lean cut (round/topside)",ck:1.37,rw:1,note:MEAT_NOTE},
      beef:{cat:"meat",n:"Beef mince, lean (90/10)",ck:1.37,rw:1,note:MEAT_NOTE},
      beef_regular:{cat:"meat",n:"Beef mince, regular (80/20)",ck:1.37,rw:1,note:MEAT_NOTE},
      lamb:{cat:"meat",n:"Lamb",ck:1.37,rw:1,note:MEAT_NOTE},
      pork:{cat:"meat",n:"Lean pork",ck:1.37,rw:1,note:MEAT_NOTE},
      kangaroo:{cat:"meat",n:"Kangaroo",ck:1.37,rw:1,note:MEAT_NOTE},
      venison:{cat:"meat",n:"Venison",ck:1.37,rw:1,note:MEAT_NOTE},
      goat:{cat:"meat",n:"Goat",ck:1.37,rw:1,note:MEAT_NOTE},
      bison:{cat:"meat",n:"Bison",ck:1.37,rw:1,note:MEAT_NOTE},
      wildboar:{cat:"meat",n:"Wild boar",ck:1.37,rw:1,note:MEAT_NOTE},
      quail:{cat:"meat",n:"Quail",ck:1.37,rw:1,note:MEAT_NOTE},
      crocodile:{cat:"meat",n:"Crocodile",ck:1.37,rw:1,note:MEAT_NOTE},
      emu:{cat:"meat",n:"Emu",ck:1.37,rw:1,note:MEAT_NOTE},
      salmon:{cat:"meat",n:"Salmon",ck:1.35,rw:1,note:"buy raw fillet — loses ~26% in the pan"},
      sardines:{cat:"meat",n:"Sardines (in water, bones in)",ck:1,rw:1,unitG:90,unitName:"tin",note:"~90 g drained per tin"},
      mackerel:{cat:"meat",n:"Mackerel",ck:1.35,rw:1,note:"buy raw fillet — loses ~26% in the pan"},
      tuna:{cat:"meat",n:"Tuna (in water)",ck:1,rw:1,unitG:95,unitName:"tin",note:"~95 g drained per tin"},
      whitefish:{cat:"meat",n:"White fish (cod)",ck:1.35,rw:1,note:"buy raw fillet — loses ~26% in the pan"},
      rabbit:{cat:"meat",n:"Rabbit",ck:1.37,rw:1,note:MEAT_NOTE},
      tripe:{cat:"meat",n:"Green tripe (raw)",ck:1,rw:1,note:"sold and fed raw"},
      egg:{cat:"meat",n:"Whole egg",ck:1,rw:1,unitG:50,unitName:"egg",note:"~50 g per large egg"},
      raw_meaty_bone:{cat:"meat",n:"Raw meaty bone (necks, wings, bone-in mince)",ck:1,rw:1,always:true,note:"fed raw — never cooked bone"},
      chicken_liver:{cat:"organ",n:"Chicken liver",ck:1.37,rw:1,note:MEAT_NOTE},
      beef_liver:{cat:"organ",n:"Beef liver",ck:1.37,rw:1,note:MEAT_NOTE},
      lamb_liver:{cat:"organ",n:"Lamb liver",ck:1.37,rw:1,note:MEAT_NOTE},
      pork_liver:{cat:"organ",n:"Pork liver",ck:1.37,rw:1,note:MEAT_NOTE},
      beef_kidney:{cat:"organ",n:"Beef kidney",ck:1.37,rw:1,note:MEAT_NOTE},
      lamb_kidney:{cat:"organ",n:"Lamb kidney",ck:1.37,rw:1,note:MEAT_NOTE},
      beef_heart:{cat:"organ",n:"Beef heart (muscle)",ck:1.37,rw:1,note:MEAT_NOTE},
      chicken_heart:{cat:"organ",n:"Chicken heart (muscle)",ck:1.37,rw:1,note:MEAT_NOTE},
      white_rice:{cat:"carb",n:"White rice",ck:0.36,rw:0.36,note:"dry weight — rice roughly triples when cooked"},
      brown_rice:{cat:"carb",n:"Brown rice",ck:0.33,rw:0.33,note:"dry weight — cooks up ~3×"},
      sweet_potato:{cat:"carb",n:"Sweet potato",ck:1.2,rw:1.2,note:"whole, unpeeled — allows for skin & trim"},
      potato:{cat:"carb",n:"Potato",ck:1.2,rw:1.2,note:"whole — allows for peel & eyes"},
      oats:{cat:"carb",n:"Oats",ck:0.18,rw:0.18,note:"dry rolled oats — porridge is ~5× the dry weight"},
      quinoa:{cat:"carb",n:"Quinoa",ck:0.32,rw:0.32,note:"dry weight — cooks up ~3×"},
      pumpkin:{cat:"carb",n:"Pumpkin",ck:1.4,rw:1.4,note:"whole — allows for skin & seeds"},
      pasta:{cat:"carb",n:"Pasta",ck:0.36,rw:0.36,note:"dry weight — cooks up ~2.5–3×"},
      carrot:{cat:"veg",n:"Carrot",ck:1.1,rw:1.1},
      green_beans:{cat:"veg",n:"Green beans",ck:1.1,rw:1.1,note:"fresh or frozen both fine"},
      broccoli:{cat:"veg",n:"Broccoli",ck:1.25,rw:1.25,note:"allows for the thick stalk"},
      spinach:{cat:"veg",n:"Spinach",ck:1.1,rw:1.1},
      peas:{cat:"veg",n:"Peas",ck:1,rw:1,note:"frozen peas are perfect"},
      zucchini:{cat:"veg",n:"Zucchini",ck:1.05,rw:1.05},
      kale:{cat:"veg",n:"Kale",ck:1.2,rw:1.2,note:"allows for tough stems"},
      cucumber:{cat:"veg",n:"Cucumber",ck:1.05,rw:1.05},
      celery:{cat:"veg",n:"Celery",ck:1.1,rw:1.1},
      cauliflower:{cat:"veg",n:"Cauliflower",ck:1.3,rw:1.3,note:"allows for leaves & core"},
      brussels:{cat:"veg",n:"Brussels sprouts",ck:1.1,rw:1.1},
      capsicum:{cat:"veg",n:"Capsicum (red, no seeds)",ck:1.2,rw:1.2,note:"allows for seeds & core"},
      beetroot:{cat:"veg",n:"Beetroot",ck:1.25,rw:1.25,note:"allows for skin & tops"},
      bokchoy:{cat:"veg",n:"Bok choy",ck:1.1,rw:1.1},
      asparagus:{cat:"veg",n:"Asparagus",ck:1.3,rw:1.3,note:"allows for woody ends"},
      cabbage:{cat:"veg",n:"Cabbage",ck:1.15,rw:1.15},
      mushrooms:{cat:"veg",n:"Mushrooms (store-bought)",ck:1.05,rw:1.05},
      corn:{cat:"veg",n:"Corn (off the cob)",ck:1,rw:1,note:"frozen kernels are easiest — never the cob"},
      blueberries:{cat:"fruit",n:"Blueberries",ck:1,rw:1},
      apple:{cat:"fruit",n:"Apple (no seeds)",ck:1.15,rw:1.15,note:"allows for core"},
      banana:{cat:"fruit",n:"Banana",ck:1.55,rw:1.55,note:"allows for peel (~35% of the fruit)"},
      watermelon:{cat:"fruit",n:"Watermelon (deseeded)",ck:1.9,rw:1.9,note:"allows for the rind"},
      strawberry:{cat:"fruit",n:"Strawberry",ck:1.05,rw:1.05},
      blackberry:{cat:"fruit",n:"Blackberries",ck:1,rw:1},
      cranberry:{cat:"fruit",n:"Cranberries",ck:1,rw:1},
      mango:{cat:"fruit",n:"Mango (no pit)",ck:1.45,rw:1.45,note:"allows for skin & pit"},
      pineapple:{cat:"fruit",n:"Pineapple (no core/skin)",ck:1.95,rw:1.95,note:"allows for skin & core"},
      raspberry:{cat:"fruit",n:"Raspberries",ck:1,rw:1},
      pear:{cat:"fruit",n:"Pear (no seeds)",ck:1.1,rw:1.1},
      rockmelon:{cat:"fruit",n:"Rockmelon (cantaloupe)",ck:1.8,rw:1.8,note:"allows for rind & seeds"},
      papaya:{cat:"fruit",n:"Papaya (no seeds)",ck:1.55,rw:1.55,note:"allows for skin & seeds"},
      peach:{cat:"fruit",n:"Peach (no pit)",ck:1.1,rw:1.1},
      apricot:{cat:"fruit",n:"Apricot (no pit)",ck:1.1,rw:1.1},
      kiwi:{cat:"fruit",n:"Kiwi (peeled)",ck:1.15,rw:1.15},
      honeydew:{cat:"fruit",n:"Honeydew melon",ck:1.8,rw:1.8,note:"allows for rind & seeds"},
      orange:{cat:"fruit",n:"Orange (peeled)",ck:1.4,rw:1.4,note:"allows for peel"},
      plum:{cat:"fruit",n:"Plum (no pit)",ck:1.05,rw:1.05},
      cherry:{cat:"fruit",n:"Cherry (no pit)",ck:1.15,rw:1.15,note:"allows for pits & stems"},
      cottage:{cat:"topper",n:"Cottage cheese",ck:1,rw:1,note:"tubs are usually 250–500 g"},
      yoghurt:{cat:"topper",n:"Plain yoghurt",ck:1,rw:1,note:"plain, unsweetened — never anything with xylitol"},
      kefir:{cat:"topper",n:"Kefir",ck:1,rw:1,note:"≈ same ml as grams"},
      bone_broth:{cat:"topper",n:"Bone broth",ck:1,rw:1,note:"≈ same ml as grams — no onion, no salt"},
      olive:{cat:"oil",n:"Olive oil",ck:1,rw:1,oilML:true},
      canola:{cat:"oil",n:"Canola oil",ck:1,rw:1,oilML:true},
      flaxseed:{cat:"oil",n:"Flaxseed oil",ck:1,rw:1,oilML:true},
      sunflower:{cat:"oil",n:"Sunflower oil",ck:1,rw:1,oilML:true},
      safflower:{cat:"oil",n:"Safflower oil",ck:1,rw:1,oilML:true},
      hemp:{cat:"oil",n:"Hemp seed oil",ck:1,rw:1,oilML:true},
      avocado:{cat:"oil",n:"Avocado oil",ck:1,rw:1,oilML:true},
      chicken_fat:{cat:"oil",n:"Chicken fat",ck:1,rw:1,oilML:true},
      beef_tallow:{cat:"oil",n:"Beef tallow",ck:1,rw:1,oilML:true},
      duck_fat:{cat:"oil",n:"Duck fat",ck:1,rw:1,oilML:true},
      ghee:{cat:"oil",n:"Ghee (clarified butter)",ck:1,rw:1,oilML:true},
      mct:{cat:"oil",n:"MCT oil",ck:1,rw:1,oilML:true},
      coconut:{cat:"oil",n:"Coconut oil",ck:1,rw:1,oilML:true}
    };
    const NAME2KEY = {};
    Object.entries(DB).forEach(([k,v])=>NAME2KEY[v.n.toLowerCase()]=k);
    // forgiving aliases for hand-typed names
    const ALIASES = {"chicken":"chicken_thigh","chicken thigh":"chicken_thigh","chicken breast":"chicken_breast",
      "beef mince":"beef","rice":"white_rice","egg":"egg","eggs":"egg","yoghurt":"yoghurt","yogurt":"yoghurt",
      "raw meaty bone":"raw_meaty_bone"};
    function matchKey(name){
      const s=name.trim().toLowerCase();
      return NAME2KEY[s] || ALIASES[s] || null;
    }


    function cfg(path, fallback){
      const parts = path.split(".");
      let value = SPC_CONFIG;
      for(const part of parts){
        if(value == null || typeof value !== "object") return fallback;
        value = value[part];
      }
      return typeof value === "string" && value.trim() ? value : fallback;
    }
    function cfgLink(){
      return (SPC_CONFIG.links && SPC_CONFIG.links.homemadeCalculator) || "/homemade-food-calculator";
    }

    /* ================= STATE ================= */
    let PID=1, IID=1;
    const state = {
      plans:[ newPlan(cfg("labels.defaultPlanName", "My dog"),"dog") ],
      prices:{}   // aggId -> number
    };
    function newPlan(name, species){
      return {id:PID++, name, species:species||"dog", method:"cooked", meals:2, items:[], supps:[]};
    }

    /* ================= PLAN UI ================= */
    const UNITS = ["g","kg","ml","cup","can","pouch","scoop","piece","tbsp"];
    function unitLabel(u,per){ return u==="g"?"g/day": u==="kg"?"kg/day": u==="ml"?"ml/day": u+(per?"/day":""); }

    function dbSelectHTML(){
      const groups = {};
      Object.entries(DB).forEach(([k,v])=>{ (groups[v.cat]=groups[v.cat]||[]).push([k,v]); });
      let h = `<option value="">${cfg("labels.addIngredient", "＋ Add an ingredient…")}</option><option value="__custom">${cfg("labels.customFood", "✏️ My own food (kibble, cans, anything)")}</option>`;
      ["meat","organ","carb","veg","fruit","topper","oil"].forEach(c=>{
        h += `<optgroup label="${CATS[c]}">` + groups[c].map(([k,v])=>`<option value="${k}">${v.n}</option>`).join("") + `</optgroup>`;
      });
      return h;
    }
    function planHTML(p){
      const del = state.plans.length>1 ? `<button class="shopping-prep-calculator__remove-plan del" data-p="${p.id}" title="Remove this plan">${cfg("labels.remove", "✕ remove")}</button>` : "";
      const items = p.items.length ? p.items.map(it=>{
        const db = it.key ? DB[it.key] : null;
        const sub = db && db.note ? `<small>${db.note}</small>` : (it.packSize?`<small>pack size: ${it.packSize} ${it.unit}</small>`:"");
        return `<div class="shopping-prep-calculator__item item">
          <div class="shopping-prep-calculator__item-name nm">${db?db.n:esc(it.name)}${sub}</div>
          <input type="number" class="shopping-prep-calculator__item-quantity qty" data-p="${p.id}" data-i="${it.id}" value="${it.perDay}" min="0" step="any">
          <span class="shopping-prep-calculator__item-unit u">${unitLabel(it.unit,true)}</span>
          <button class="shopping-prep-calculator__remove-item rm" data-p="${p.id}" data-i="${it.id}" title="Remove">✕</button>
        </div>`;
      }).join("") : `<div style="font-size:13px;color:var(--mut);padding:6px 0">${cfg("labels.noFoods", "No foods yet — paste a recipe below or add ingredients.")}</div>`;
      const suppBadge = p.supps.length ? `<span style="font-size:12px;color:var(--teal-d);font-weight:700">✓ ${p.supps.length} supplements imported for the supplies check</span>` : "";
      return `<div class="shopping-prep-calculator__plan" data-plan="${p.id}">
        <div class="shopping-prep-calculator__plan-header">
          <input type="text" class="shopping-prep-calculator__plan-name pname" data-p="${p.id}" value="${p.name.replace(/"/g,"&quot;")}" placeholder="Pet's name">
          <div class="shopping-prep-calculator__segments mseg" data-p="${p.id}">
            <button data-m="cooked" class="shopping-prep-calculator__segment ${p.method==="cooked"?"is-active":""}">🍳 Cooked</button>
            <button data-m="raw" class="shopping-prep-calculator__segment ${p.method==="raw"?"is-active":""}">🥩 Raw</button>
            <button data-m="commercial" class="shopping-prep-calculator__segment ${p.method==="commercial"?"is-active":""}">🥫 Commercial</button>
          </div>
          <select class="shopping-prep-calculator__plan-meals meals" data-p="${p.id}" style="max-width:120px" title="Meals ${cfg("labels.amountPlaceholder", "per day")}">
            ${[1,2,3,4].map(m=>`<option value="${m}"${p.meals===m?" selected":""}>${m} meal${m>1?"s":""}/day</option>`).join("")}
          </select>
          ${del}
        </div>
        <div class="shopping-prep-calculator__items items">${items}</div>
        <div style="margin-top:4px">${suppBadge}</div>
        <div class="shopping-prep-calculator__add-row addrow">
          <select class="shopping-prep-calculator__add-select addSel" data-p="${p.id}">${dbSelectHTML()}</select>
          <input type="text" class="shopping-prep-calculator__custom-name cname" data-p="${p.id}" placeholder="${cfg("labels.foodNamePlaceholder", "Food name, e.g. Salmon kibble")}" style="display:none">
          <input type="number" class="shopping-prep-calculator__custom-amount camt" data-p="${p.id}" placeholder="${cfg("labels.amountPlaceholder", "per day")}" min="0" step="any">
          <select class="shopping-prep-calculator__custom-unit unitSel" data-p="${p.id}" style="display:none">${UNITS.map(u=>`<option value="${u}">${u}</option>`).join("")}</select>
          <input type="number" class="shopping-prep-calculator__custom-pack cpack" data-p="${p.id}" placeholder="${cfg("labels.packSizePlaceholder", "pack size (optional)")}" min="0" step="any" style="display:none;max-width:130px">
          <button class="shopping-prep-calculator__secondary-button shopping-prep-calculator__secondary-button--small shopping-prep-calculator__add-item addBtn" data-p="${p.id}">${cfg("labels.add", "Add")}</button>
        </div>
        <details class="shopping-prep-calculator__importer importer" style="margin-top:12px">
          <summary>${cfg("labels.importSummary", "Paste a recipe from the Homemade Food Calculator")}</summary>
          <div class="shopping-prep-calculator__import-box import-box">
            <div style="font-size:12.5px;color:var(--mut);margin-bottom:8px">In the <a href="${cfgLink()}" style="color:var(--teal-d);font-weight:700">Homemade Food Calculator</a>, tap <strong>📋 Copy recipe</strong>, then paste the whole thing here. Ingredients, method, meals ${cfg("labels.amountPlaceholder", "per day")} and the supplement list all load automatically.</div>
            <textarea class="shopping-prep-calculator__paste-box pasteBox" data-p="${p.id}" placeholder="ASK A VET™ — HOMEMADE FOOD CALCULATOR&#10;By Dr Duncan Houston&#10;...&#10;INGREDIENTS (${cfg("labels.amountPlaceholder", "per day")})&#10;- Chicken thigh (no skin): 213 g&#10;..."></textarea>
            <div style="display:flex;gap:10px;align-items:center;margin-top:8px;flex-wrap:wrap">
              <button class="shopping-prep-calculator__primary-button shopping-prep-calculator__secondary-button--small parseBtn" data-p="${p.id}">${cfg("labels.importButton", "Import recipe")}</button>
              <span class="shopping-prep-calculator__import-status stat" data-p="${p.id}"></span>
            </div>
          </div>
        </details>
      </div>`;
    }
    function renderPlans(){
      document.getElementById("plans").innerHTML = state.plans.map(planHTML).join("");
    }
    function getPlan(id){ return state.plans.find(p=>p.id===+id); }

    /* ================= IMPORT PARSER ================= */
    function parseRecipeText(txt, plan){
      const lines = txt.split(/\r?\n/);
      let mode = "", found = 0, missed = [];
      const items = [], supps = [];
      const mMethod = txt.match(/\((cooked|raw),/i);
      const mMeals  = txt.match(/[x×]\s*(\d+)\s*meals/i);
      const mSpecies = txt.match(/kg\s+(dog|cat)\b/i);
      for(const raw of lines){
        const line = raw.trim();
        if(/^INGREDIENTS/i.test(line)){ mode="ing"; continue; }
        if(/^DAILY SUPPLEMENTS/i.test(line)){ mode="supp"; continue; }
        if(/^NOTES/i.test(line)){ mode=""; continue; }
        if(!line.startsWith("-")) continue;
        if(mode==="ing"){
          const m = line.match(/^-\s*(.+?):\s*([\d.]+)\s*g\b/);
          if(m){
            const key = matchKey(m[1]);
            const g = parseFloat(m[2]);
            if(key) items.push({key, name:DB[key].n, g});
            else { items.push({key:null, name:m[1].trim(), g}); missed.push(m[1].trim()); }
            found++;
          }
        } else if(mode==="supp"){
          const body = line.replace(/^-\s*/,"");
          const bone = body.match(/raw meaty bone:?\s*~?([\d.]+)\s*g/i);
          if(bone){ items.push({key:"raw_meaty_bone", name:DB.raw_meaty_bone.n, g:parseFloat(bone[1])}); found++; }
          else supps.push(body);
        }
      }
      if(!found) return {ok:false, msg:"Couldn't find an ingredient list — paste the full text from the calculator's Copy recipe button."};
      if(mMethod) plan.method = mMethod[1].toLowerCase();
      if(mMeals)  plan.meals  = Math.min(4, Math.max(1, parseInt(mMeals[1])));
      if(mSpecies){
        plan.species = mSpecies[1].toLowerCase();
        if(/^my (dog|cat|pet)$/i.test(plan.name)){
          const nm = "My "+plan.species;
          if(!state.plans.some(o=>o!==plan && o.name.toLowerCase()===nm.toLowerCase())) plan.name = nm;
        }
      }
      plan.items = items.map(it=>({id:IID++, key:it.key, name:it.name, perDay:Math.round(it.g), unit:"g", packSize:0}));
      plan.supps = supps;
      const msg = `✓ Imported ${plan.items.length} ingredients (${plan.method}, ${plan.meals} meals/day)` +
        (supps.length?` + ${supps.length} supplement${supps.length>1?"s":""}`:"") +
        (missed.length?` — ${missed.length} unrecognised kept as custom: ${missed.join(", ")}`:"");
      return {ok:true, msg};
    }

    /* ================= COMPUTE ================= */
    function settings(){
      return {
        days: Math.max(1, parseInt(document.getElementById("days").value)||7),
        buf: (parseInt(document.getElementById("buffer").value)||0)/100,
        rawW: document.getElementById("rawWeights").checked,
        cost: document.getElementById("costOn").checked,
        cur: document.getElementById("currency").value
      };
    }
    function itemFactor(it, plan, rawW){
      if(!rawW) return 1;
      if(!it.key) return 1;
      const db = DB[it.key];
      return plan.method==="cooked" ? db.ck : db.rw;   // commercial has no DB items realistically, factor rw=ck anyway
    }
    const WEIGHT_UNITS = {g:1, kg:1000, ml:1, tbsp:13.5};
    function compute(){
      const s = settings();
      const aggs = {};   // id -> agg
      const plansOut = [];
      state.plans.forEach(plan=>{
        let fedG = 0; const unitFoods=[];
        plan.items.forEach(it=>{
          const isW = it.key ? true : (it.unit in WEIGHT_UNITS);
          if(isW){
            const gPerDay = it.key ? it.perDay : it.perDay*WEIGHT_UNITS[it.unit];
            if(!(gPerDay>0)) return;
            fedG += gPerDay;
            const f = itemFactor(it, plan, s.rawW);
            const id = it.key ? "k:"+it.key : "c:"+it.name.toLowerCase();
            const db = it.key ? DB[it.key] : null;
            const a = aggs[id] || (aggs[id]={id, type:"w", name: db?db.n:it.name, cat: db?db.cat:"mine",
              db, fedPerDay:0, buyRaw:0, pets:new Set()});
            a.fedPerDay += gPerDay;
            a.buyRaw += gPerDay * f * s.days * (1+s.buf);
            a.fedTotalBuf = (a.fedTotalBuf||0) + gPerDay * s.days * (1+s.buf);
            a.pets.add(plan.name);
          } else {
            if(!(it.perDay>0)) return;
            unitFoods.push(it);
            const id = "u:"+it.name.toLowerCase()+":"+it.unit;
            const a = aggs[id] || (aggs[id]={id, type:"u", name:it.name, cat:"units", unit:it.unit,
              perDay:0, packSize:it.packSize||0, pets:new Set()});
            a.perDay += it.perDay;
            if(it.packSize) a.packSize = it.packSize;
            a.pets.add(plan.name);
          }
        });
        plansOut.push({plan, fedG, unitFoods});
      });
      // finalise
      const list = Object.values(aggs);
      list.forEach(a=>{
        if(a.type==="u"){
          a.totalRaw = a.perDay * s.days * (1+s.buf);
          a.totalUnits = Math.ceil(a.totalRaw);
          if(a.packSize) a.packs = Math.ceil(a.totalRaw/a.packSize);
        }
      });
      const order = ["meat","organ","carb","veg","fruit","topper","oil","mine","units"];
      list.sort((x,y)=> order.indexOf(x.cat)-order.indexOf(y.cat) || x.name.localeCompare(y.name));
      return {s, list, plansOut};
    }

    /* ================= FORMAT HELPERS ================= */
    function roundUpG(g){
      if(g<=0) return 0;
      if(g<100) return Math.ceil(g/5)*5;
      if(g<500) return Math.ceil(g/10)*10;
      if(g<2000) return Math.ceil(g/50)*50;
      return Math.ceil(g/100)*100;
    }
    function fmtW(g){ return g>=1000 ? (g/1000).toFixed(g>=10000?1:2).replace(/\.0+$/,"")+" kg" : Math.round(g)+" g"; }
    function fmtDay(g){ return g>=1000 ? (g/1000).toFixed(2).replace(/0+$/,"").replace(/\.$/,"")+" kg" : Math.round(g)+" g"; }
    function esc(t){ return String(t).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/"/g,"&quot;"); }
    function plural(u,n){ return n===1 ? u : (/(ch|sh|s|x)$/.test(u) ? u+"es" : u+"s"); }
    function money(cur,v){ return cur + (v>=100? Math.round(v).toLocaleString() : v.toFixed(2)); }

    function buyCellHTML(a, s){
      if(a.type==="u"){
        let main = `${a.totalUnits} ${plural(a.unit,a.totalUnits)}`;
        let prac = `${a.perDay % 1 ? a.perDay.toFixed(1) : a.perDay} ${a.unit}/day`;
        if(a.packSize) main = `${a.packs} pack${a.packs!==1?"s":""}`, prac = `${a.totalUnits} ${plural(a.unit,a.totalUnits)} total · pack of ${a.packSize}`;
        return `<span class="shopping-prep-calculator__buy-value">${main}</span><span class="shopping-prep-calculator__practical-note">${prac}</span>`;
      }
      const g = roundUpG(a.buyRaw);
      let main = fmtW(g), prac = [];
      const db = a.db;
      if(db && db.unitG){
        const n = Math.ceil(a.buyRaw/db.unitG);
        main = `${n} ${db.unitName}${n!==1?"s":""}`;
        prac.push(`≈ ${fmtW(g)}`);
      }
      if(db && db.oilML){
        const ml = Math.ceil(a.buyRaw/0.92/10)*10;
        main = `${ml} ml`;
        prac.push(`≈ ${(a.fedPerDay/13.5).toFixed(1)} tbsp/day`);
      }
      const converted = Math.abs(a.buyRaw - a.fedTotalBuf) > a.fedTotalBuf*0.01;
      if(db && db.note && (converted || db.unitG || db.oilML || db.always)) prac.push(db.note);
      if(!db && s.rawW) prac.push("your food — counted as entered");
      return `<span class="shopping-prep-calculator__buy-value">${main}</span>${prac.length?`<span class="shopping-prep-calculator__practical-note">${prac.join(" · ")}</span>`:""}`;
    }
    function aggCost(a, s){
      const p = state.prices[a.id];
      if(!(p>0)) return null;
      if(a.type==="u") return a.packSize ? p*a.packs : p*a.totalUnits;
      return p * (a.buyRaw/1000);
    }

    /* ================= RENDER RESULTS ================= */
    let LAST=null;
    function calculate(scroll){
      const plansWithFood = state.plans.filter(p=>p.items.some(i=>i.perDay>0));
      if(!plansWithFood.length){
        if(scroll) alert(cfg("validation.missingFood", "Add at least one food first — paste a recipe or add an ingredient."));
        document.getElementById("results").hidden=true; LAST=null;   // don't leave stale results up
        return;
      }
      const R = compute(); LAST = R;
      const {s, list, plansOut} = R;

      const petNames = plansWithFood.map(p=>esc(p.name)).join(", ");
      document.getElementById("shopSummary").innerHTML =
        `Everything to buy for <strong>${petNames}</strong> for <strong>${s.days} day${s.days>1?"s":""}</strong>` +
        (s.buf?` with a <strong>+${Math.round(s.buf*100)}% buffer</strong>`:"") +
        (s.rawW?`, converted to <strong>purchase weights</strong> (raw meat, dry rice, whole veg):`:":");

      // KPIs
      const totBuyG = list.filter(a=>a.type==="w").reduce((t,a)=>t+a.buyRaw,0);
      const totFedG = plansOut.reduce((t,p)=>t+p.fedG,0);
      let totCost = 0, priced=0;
      list.forEach(a=>{ const c=aggCost(a,s); if(c!=null){ totCost+=c; priced++; } });
      const kpis = [
        kpi(s.days, cfg("labels.daysPlanned", "days planned")),
        kpi(fmtW(roundUpG(totBuyG)), cfg("labels.totalToBuy", "total to buy")),
        kpi(fmtDay(totFedG)+"/day", cfg("labels.foodFedDaily", "food fed daily")),
        s.cost ? kpi(priced? money(s.cur,totCost) : "—", priced?`est. cost (${money(s.cur,totCost/s.days)}/day)`:"add prices below")
               : kpi(plansWithFood.length, plansWithFood.length>1?cfg("labels.pets", "pets"):cfg("labels.pet", "pet"))
      ];
      document.getElementById("kpis").innerHTML = kpis.join("");

      // table
      document.getElementById("shopHead").innerHTML =
        `<th>Ingredient</th><th class="num">Fed / day</th><th class="num">Fed over ${s.days}d</th><th class="num">🛒 Buy</th>` +
        (s.cost?`<th class="num no-print">Price / ${"kg·unit"}</th><th class="num">Cost</th>`:"");
      const tb = document.querySelector("#shopTable tbody");
      let html = "", curCat = "";
      const multi = plansWithFood.length>1;
      list.forEach(a=>{
        if(a.cat!==curCat){ curCat=a.cat; html += `<tr class="shopping-prep-calculator__category-row"><td colspan="${s.cost?6:4}">${CATS[a.cat]}</td></tr>`; }
        const petTag = multi ? ` <span style="color:var(--mut);font-size:11.5px">(${[...a.pets].map(esc).join(" + ")})</span>` : "";
        const fedDay = a.type==="w" ? fmtDay(a.fedPerDay) : `${a.perDay%1?a.perDay.toFixed(1):a.perDay} ${a.unit}`;
        const fedTot = a.type==="w" ? fmtW(a.fedPerDay*s.days) : `${Math.ceil(a.perDay*s.days)} ${plural(a.unit,Math.ceil(a.perDay*s.days))}`;
        const costCells = s.cost ? (()=>{
          const val = state.prices[a.id]||"";
          const c = aggCost(a,s);
          const per = a.type==="u" ? (a.packSize?"/pack":"/"+a.unit) : "/kg";
          return `<td class="num no-print"><input type="number" class="shopping-prep-calculator__price-input price" data-agg="${esc(a.id)}" value="${val}" min="0" step="any" placeholder="0"><span style="font-size:11px;color:var(--mut)">${per}</span></td>
            <td class="num"><span class="shopping-prep-calculator__cost-value costv" data-agg="${esc(a.id)}">${c!=null?money(s.cur,c):"—"}</span></td>`;
        })() : "";
        html += `<tr><td>${esc(a.name)}${petTag}</td><td class="num">${fedDay}</td><td class="num">${fedTot}</td><td class="num">${buyCellHTML(a,s)}</td>${costCells}</tr>`;
      });
      html += `<tr class="shopping-prep-calculator__total-row"><td>Total${s.rawW?" (purchase weight)":""}</td><td class="num">${fmtDay(totFedG)}</td><td class="num">${fmtW(totFedG*s.days)}</td><td class="num">${fmtW(roundUpG(totBuyG))}</td>${s.cost?`<td class="no-print"></td><td class="num"><span data-spc-total-cost>${priced?money(s.cur,totCost):"—"}</span></td>`:""}</tr>`;
      tb.innerHTML = html;
      document.getElementById("shopLegend").textContent =
        s.rawW ? "Buy amounts include the buffer and real-world conversions: raw weight for meat you'll cook, dry weight for rice/oats/pasta, and whole-item weight for veg and fruit with peel, cores or rinds."
               : "Buy amounts are the fed amounts plus your buffer — purchase-weight conversion is off, so weigh food after cooking to match the recipe.";

      renderSupps(R); renderPrep(R);
      document.getElementById("results").hidden = false;
      if(scroll) document.getElementById("results").scrollIntoView({behavior:"smooth", block:"start"});
    }
    function kpi(v,l){ return `<div class="shopping-prep-calculator__kpi"><div class="shopping-prep-calculator__kpi-value">${v}</div><div class="shopping-prep-calculator__kpi-label">${l}</div></div>`; }
    function recalc(){ if(LAST) calculate(false); }

    /* ---- supplement supplies check ---- */
    function suppTotal(line, days){
      const caps = line.match(/~?(\d+)\s*[×x]\s*1000\s*mg cap/i);
      if(caps) return `${parseInt(caps[1])*days} × 1000 mg caps for ${days} days`;
      const m = line.match(/:\s*~?([\d.]+)(?:\s*[–\-]\s*[\d.]+)?\s*(g|mg|IU|ml)\b/);
      if(m){
        let q = parseFloat(m[1])*days, u = m[2];
        if(u==="mg" && q>=1000){ q=q/1000; u="g"; }
        const qs = q>=100 ? Math.round(q).toLocaleString() : (Math.round(q*10)/10);
        return `≈ ${qs} ${u} total for ${days} days`;
      }
      return `daily dose × ${days} days — check the tub`;
    }
    function renderSupps(R){
      const withSupps = R.plansOut.filter(p=>p.plan.supps.length);
      const card = document.getElementById("suppCard");
      if(!withSupps.length){ card.hidden=true; return; }
      card.hidden = false;
      document.getElementById("suppList").innerHTML = withSupps.map(({plan})=>{
        const rows = plan.supps.map(sp=>{
          const label = sp.split(":")[0];
          return `<div class="shopping-prep-calculator__supplement-item supp"><div class="shopping-prep-calculator__item-icon ic">💊</div><div><div class="shopping-prep-calculator__item-title amt">${esc(label)}</div><div class="shopping-prep-calculator__item-text why">${esc(sp)} → <strong>${suppTotal(sp, R.s.days)}</strong></div></div></div>`;
        }).join("");
        return `<div style="margin-top:6px"><div class="shopping-prep-calculator__notes-heading notes-head" style="margin-bottom:4px">${esc(plan.name)}</div>${rows}</div>`;
      }).join("");
    }

    /* ---- meal prep plan ---- */
    function renderPrep(R){
      const {s} = R;
      document.getElementById("prepHint").innerHTML =
        `How to turn the shopping into <strong>${s.days} day${s.days>1?"s":""}</strong> of ready-to-serve meals without anything spoiling.`;
      const out = R.plansOut.filter(p=>p.plan.items.some(i=>i.perDay>0)).map(({plan,fedG,unitFoods})=>{
        const perMeal = fedG/plan.meals;
        const fridgeDays = plan.method==="raw" ? 2 : 3;
        const freezeDays = Math.max(0, s.days - fridgeDays);
        const facts = plan.method==="commercial" ? [
          fact(fmtDay(fedG), "fed per day"),
          fact(`${Math.round(perMeal)} g × ${plan.meals}`, "per meal"),
          fact(fmtW(fedG*s.days), `for ${s.days} days`)
        ] : [
          fact(fmtDay(fedG), "fed per day"),
          fact(`${Math.round(perMeal)} g × ${plan.meals}`, "per meal"),
          fact(`${s.days}`, "daily tubs"),
          fact(freezeDays? `${Math.min(freezeDays,s.days)} frozen · ${Math.min(fridgeDays,s.days)} fridge` : "all fridge", "storage split")
        ];
        let steps=[], extra="";
        const tubMl = Math.max(250, Math.ceil(fedG*1.15/250)*250);  // container size with headroom
        const spaceL = Math.ceil(fedG*s.days/1000 * 1.2);           // fridge+freezer space incl. tubs
        if(plan.method==="cooked"){
          const batchKg = fedG*s.days/1000;
          steps = [
            `<strong>One cook-up:</strong> cook the full batch plain — no salt, onion, garlic or stock cubes. You're making ≈ <strong>${batchKg.toFixed(1)} kg</strong> of finished food (allow ≈ ${spaceL} L of fridge + freezer space).`,
            `<strong>Cool → mix → portion</strong> into ${s.days} daily tubs of ≈ ${Math.round(fedG)} g — ${tubMl >= 1000 ? (tubMl/1000)+" L" : tubMl+" ml"} containers fit nicely (serve as ${plan.meals} × ${Math.round(perMeal)} g meals). Mix the calcium through the cooled batch.`,
            `<strong>Store:</strong> ${Math.min(fridgeDays,s.days)} tub${Math.min(fridgeDays,s.days)>1?"s":""} in the fridge${freezeDays?`, ${freezeDays} in the freezer (airtight, best within 2 months)`:""}.`,
            `<strong>Thaw routine:</strong> every evening, move tomorrow's tub from freezer to fridge. Never thaw on the bench.`,
            `<strong>At serving:</strong> stir oils, fish oil, probiotics and vitamins in fresh — heat and freezing degrade them.`
          ];
        } else if(plan.method==="raw"){
          const batchKg = fedG*s.days/1000;
          steps = [
            `<strong>Portion day:</strong> divide ≈ <strong>${batchKg.toFixed(1)} kg</strong> into ${s.days} daily tubs of ≈ ${Math.round(fedG)} g — ${tubMl >= 1000 ? (tubMl/1000)+" L" : tubMl+" ml"} containers fit nicely (${plan.meals} × ${Math.round(perMeal)} g meals). Allow ≈ ${spaceL} L of freezer space.`,
            `<strong>Freeze first, always:</strong> meat deep-frozen ≥ 3 weeks before feeding kills most parasites — buy ahead so the freezer time is done before day 1.`,
            `<strong>Store:</strong> max ${fridgeDays} days' worth thawed in the fridge; everything else stays frozen. Thaw overnight in the fridge.`,
            `<strong>Hygiene:</strong> separate board and utensils, hot soapy water on hands, bowls and benches after every meal. Bin anything uneaten after 30 minutes.`,
            `<strong>At serving:</strong> supplements and oils go in fresh each meal.`
          ];
        } else {
          steps = [
            `<strong>Dry food:</strong> keep it in the original bag inside an airtight container, out of the sun. Use within ~6 weeks of opening — the fats oxidise after that.`,
            `<strong>Cans & pouches:</strong> once opened, cover and refrigerate; use within 2–3 days. Serve at room temperature, not fridge-cold.`,
            `<strong>Portion control:</strong> weigh with scales, not scoops — cup measures over-serve by 20% on average, and that's how pets get fat.`,
            `<strong>Buy rhythm:</strong> don't stockpile more than ~2 months of dry food; freshness beats bulk discounts.`
          ];
        }
        if(plan.species==="cat")
          steps.push(`<strong>Cat rule — serve it warm:</strong> most cats refuse fridge-cold food. Let thawed food come to room temperature or warm it slightly (never hot), and stir before serving.`);
        if(fedG===0 && plan.method!=="commercial")
          steps = [`<strong>Nothing to batch-cook:</strong> this pet's foods are all counted in packs and units, so there's no cook-up — follow each product's own storage rules and serve fresh daily.`];
        if(unitFoods.length && plan.method!=="commercial")
          extra = `<div class="legend">Plus served fresh daily: ${unitFoods.map(u=>`${esc(u.name)} (${u.perDay} ${u.unit}/day)`).join(", ")}.</div>`;
        const icon = plan.method==="cooked"?"🍳":plan.method==="raw"?"🥩":"🥫";
        return `<div class="shopping-prep-calculator__prep-plan"><h3>${icon} ${esc(plan.name)} — ${plan.method}</h3>
          ${fedG?`<div class="shopping-prep-calculator__facts">${facts.join("")}</div>`:""}
          <ul class="shopping-prep-calculator__steps">${steps.map(st=>`<li>${st}</li>`).join("")}</ul>${extra}</div>`;
      }).join("");
      document.getElementById("prepPlans").innerHTML = out;

      const notes=[];
      if(s.days>60 && R.plansOut.some(p=>p.plan.method!=="commercial"))
        notes.push({t:"warn",h:"That's a long prep window",
          b:"Frozen cooked food is best within 2 months. For "+s.days+" days, split it into two cook-ups a month apart rather than one mega-batch — fresher food, and you'll refine the recipe on round two."});
      if(R.plansOut.some(p=>p.plan.species==="cat" && p.plan.items.some(i=>i.perDay>0)))
        notes.push({t:"warn",h:"If your cat refuses the new batch, don't wait them out",
          b:"Dogs can skip a meal or two and be fine. Cats can't — going unfed for more than ~24–48 hours (especially overweight cats) risks hepatic lipidosis, a life-threatening liver disease. If a cat turns their nose up at a batch: warm it, top with something they love, offer the old food, and call your vet if they're still not eating by the second day. Never try to starve a cat into accepting new food."});
      if(R.plansOut.some(p=>p.plan.method==="raw"))
        notes.push({t:"crit",h:"Raw feeding hygiene is not optional",
          b:"Salmonella and Campylobacter don't care how premium the meat was. No raw feeding in homes with infants, pregnant, elderly or immunocompromised people."});
      notes.push({t:"info",h:"Keep the supplements going every single day",
        b:"A batch-cooked diet is only balanced with its daily supplement stack. If you run out of calcium or taurine mid-window, that's when deficiencies start — the supplies check above tells you what the window uses."});
      notes.push({t:"info",h:"Watch the pet, not just the plan",
        b:"Weigh every 2 weeks and body-condition check by feel. Appetite off, vomiting, or diarrhoea on a new batch? Stop that batch and log it in ASK A VET™ — food gone wrong shows up in the bowl first."});
      document.getElementById("prepNotes").innerHTML =
        `<div class="shopping-prep-calculator__notes-heading notes-head" style="margin-top:14px">Before you cook</div>` +
        notes.map(x=>`<div class="shopping-prep-calculator__notice shopping-prep-calculator__notice--${x.t==="crit"?"danger":x.t==="warn"?"warning":"info"}"><strong>${x.h}.</strong> ${x.b}</div>`).join("");
    }
    function fact(v,l){ return `<div class="shopping-prep-calculator__fact">${v}<small>${l}</small></div>`; }

    /* ================= COPY TEXT ================= */
    function buildShoppingText(){
      if(!LAST) return "";
      const {s, list, plansOut} = LAST;
      const L=[];
      L.push("ASK A VET(TM) - PET FOOD SHOPPING LIST");
      L.push("By Dr Duncan Houston");
      L.push("");
      L.push(`FOR: ${plansOut.map(p=>p.plan.name).join(", ")} | ${s.days} days` + (s.buf?` (+${Math.round(s.buf*100)}% buffer)`:""));
      L.push("");
      let curCat="";
      let totCost=0, priced=0;
      list.forEach(a=>{
        if(a.cat!==curCat){ curCat=a.cat; L.push(CATS[a.cat].toUpperCase()); }
        let buy;
        if(a.type==="u") buy = a.packSize ? `${a.packs} packs (${a.totalUnits} ${plural(a.unit,a.totalUnits)})` : `${a.totalUnits} ${plural(a.unit,a.totalUnits)}`;
        else {
          const db=a.db;
          if(db && db.unitG) buy = `${Math.ceil(a.buyRaw/db.unitG)} ${db.unitName}s (~${fmtW(roundUpG(a.buyRaw))})`;
          else if(db && db.oilML) buy = `${Math.ceil(a.buyRaw/0.92/10)*10} ml`;
          else buy = fmtW(roundUpG(a.buyRaw));
        }
        const c = aggCost(a,s); if(c!=null){ totCost+=c; priced++; }
        L.push(`[ ] ${a.name}: ${buy}` + (c!=null?` - ${money(s.cur,c)}`:""));
      });
      const totBuyG = list.filter(a=>a.type==="w").reduce((t,a)=>t+a.buyRaw,0);
      L.push("");
      L.push(`TOTAL: ~${fmtW(roundUpG(totBuyG))} to buy` + (priced?` | est. ${money(s.cur,totCost)} (${money(s.cur,totCost/s.days)}/day)`:""));
      const withSupps = plansOut.filter(p=>p.plan.supps.length);
      if(withSupps.length){
        L.push("");
        L.push("SUPPLEMENT SUPPLIES CHECK");
        withSupps.forEach(({plan})=>{
          plan.supps.forEach(sp=> L.push(`[ ] ${plan.name} - ${sp.split(":")[0]}: ${suppTotal(sp,s.days)}`));
        });
      }
      L.push("");
      L.push("STORAGE: cooked food 3 days fridge / 2 months freezer in daily tubs; raw 2 days fridge; thaw overnight in the fridge, never the bench.");
      L.push("");
      L.push("Made with the free ASK A VET(TM) Shopping List & Meal Prep Calculator");
      return L.join("\n");
    }

    /* ================= EVENTS ================= */
    const plansEl = document.getElementById("plans");
    plansEl.addEventListener("input", e=>{
      const t=e.target;
      if(t.classList.contains("pname")){
        const p=getPlan(t.dataset.p);
        p.name = t.value || "My pet";
        // quiet species inference from the name — drives cat-specific prep advice only
        if(/\bcat\b|\bkitten\b/i.test(p.name)) p.species="cat";
        else if(/\bdog\b|\bpup(py)?\b/i.test(p.name)) p.species="dog";
        recalc();
      }
      if(t.classList.contains("qty")){
        const p=getPlan(t.dataset.p); const it=p.items.find(i=>i.id===+t.dataset.i);
        if(it) it.perDay = Math.max(0, parseFloat(t.value)||0);
        recalc();
      }
    });
    plansEl.addEventListener("change", e=>{
      const t=e.target;
      if(t.classList.contains("meals")){ getPlan(t.dataset.p).meals = parseInt(t.value); recalc(); }
      if(t.classList.contains("addSel")){
        const wrap = t.closest(".addrow");
        const custom = t.value==="__custom";
        wrap.querySelector(".cname").style.display = custom?"":"none";
        wrap.querySelector(".unitSel").style.display = custom?"":"none";
        wrap.querySelector(".cpack").style.display = custom?"":"none";
      }
    });
    plansEl.addEventListener("click", e=>{
      const b=e.target.closest("button"); if(!b) return;
      if(b.classList.contains("rm")){
        const p=getPlan(b.dataset.p); p.items = p.items.filter(i=>i.id!==+b.dataset.i);
        renderPlans(); recalc(); return;
      }
      if(b.classList.contains("del")){
        if(!confirm(cfg("validation.removePlan", "Remove this pet's plan?"))) return;
        state.plans = state.plans.filter(p=>p.id!==+b.dataset.p);
        renderPlans(); recalc(); return;
      }
      if(b.classList.contains("addBtn")){
        const p=getPlan(b.dataset.p); const wrap=b.closest(".addrow");
        const sel=wrap.querySelector(".addSel");
        const amt=parseFloat(wrap.querySelector(".camt").value)||0;
        if(sel.value==="__custom"){
          const name=wrap.querySelector(".cname").value.trim();
          if(!name){ alert(cfg("validation.missingFoodName", "Give your food a name first.")); return; }
          if(!(amt>0)){ alert(cfg("validation.missingAmount", "How much per day?")); return; }
          const unit=wrap.querySelector(".unitSel").value;
          const pack=parseFloat(wrap.querySelector(".cpack").value)||0;
          const key=matchKey(name);
          if(key && (unit==="g"||unit==="kg")) p.items.push({id:IID++, key, name:DB[key].n, perDay: unit==="kg"?amt*1000:amt, unit:"g", packSize:0});
          else p.items.push({id:IID++, key:null, name, perDay:amt, unit, packSize:pack});
        } else if(sel.value){
          if(!(amt>0)){ alert("How many grams per day? (Check the recipe or the Homemade Food Calculator.)"); return; }
          p.items.push({id:IID++, key:sel.value, name:DB[sel.value].n, perDay:amt, unit:"g", packSize:0});
        } else { alert(cfg("validation.missingIngredient", "Pick an ingredient first.")); return; }
        renderPlans(); recalc(); return;
      }
      if(b.classList.contains("parseBtn")){
        const p=getPlan(b.dataset.p);
        const box=plansEl.querySelector(`.pasteBox[data-p="${p.id}"]`);
        const res=parseRecipeText(box.value||"", p);
        if(res.ok){ renderPlans(); recalc();
          const st=plansEl.querySelector(`.plan[data-plan="${p.id}"] .stat`);
          const det=plansEl.querySelector(`.plan[data-plan="${p.id}"] details.importer`);
          if(det) det.open=true;
          if(st){ st.textContent=res.msg; st.className="shopping-prep-calculator__import-status stat is-success"; }
        } else {
          const st=plansEl.querySelector(`.stat[data-p="${p.id}"]`);
          st.textContent=res.msg; st.className="shopping-prep-calculator__import-status stat is-error";
        }
        return;
      }
      const seg=b.closest(".mseg");
      if(seg){
        const p=getPlan(seg.dataset.p);
        p.method=b.dataset.m;
        seg.querySelectorAll("button").forEach(x=>{ const active=x===b; x.classList.toggle("is-active",active); x.setAttribute("aria-pressed",active?"true":"false"); });
        recalc(); return;
      }
    });
    document.getElementById("addPlan").addEventListener("click", ()=>{
      state.plans.push(state.plans.length===1 ? newPlan(cfg("labels.defaultCatPlanName", "My cat"),"cat") : newPlan("Pet "+(state.plans.length+1),"dog"));
      renderPlans();
    });
    document.getElementById("days").addEventListener("input", ()=>{
      const v=parseInt(document.getElementById("days").value);
      document.querySelectorAll(".daysChip").forEach(c=>c.classList.toggle("is-selected", +c.dataset.d===v));
      recalc();
    });
    document.querySelectorAll(".daysChip").forEach(c=>c.addEventListener("click", ()=>{
      document.getElementById("days").value=c.dataset.d;
      document.querySelectorAll(".daysChip").forEach(x=>x.classList.toggle("is-selected",x===c));
      recalc();
    }));
    document.getElementById("buffer").addEventListener("change", recalc);
    document.getElementById("rawWeights").addEventListener("change", recalc);
    document.getElementById("costOn").addEventListener("change", ()=>{
      document.getElementById("curWrap").hidden = !document.getElementById("costOn").checked;
      recalc();
    });
    document.getElementById("currency").addEventListener("change", recalc);
    document.getElementById("calcBtn").addEventListener("click", ()=>calculate(true));
    document.getElementById("printBtn").addEventListener("click", ()=>window.print());
    // live price editing without rebuilding the table (keeps focus in the input)
    document.querySelector("#shopTable").addEventListener("input", e=>{
      const t=e.target;
      if(!t.classList.contains("price")) return;
      state.prices[t.dataset.agg] = parseFloat(t.value)||0;
      if(!LAST) return;
      const {s,list} = LAST;
      let tot=0, priced=0;
      list.forEach(a=>{
        const c=aggCost(a,s);
        const cell=document.querySelector(`.costv[data-agg="${CSS.escape(a.id)}"]`);
        if(cell) cell.textContent = c!=null?money(s.cur,c):"—";
        if(c!=null){ tot+=c; priced++; }
      });
      const totEl=document.getElementById("totCost");
      if(totEl) totEl.textContent = priced?money(s.cur,tot):"—";
      const kpiEl=document.querySelectorAll("[data-spc-kpis] .shopping-prep-calculator__kpi")[3];
      if(kpiEl && s.cost){ kpiEl.querySelector(".shopping-prep-calculator__kpi-value").textContent = priced?money(s.cur,tot):"—";
        kpiEl.querySelector(".shopping-prep-calculator__kpi-label").textContent = priced?`est. cost (${money(s.cur,tot/s.days)}/day)`:"add prices below"; }
    });
    document.getElementById("copyBtn").addEventListener("click", async()=>{
      const txt=buildShoppingText();
      if(!txt){ alert(cfg("validation.copy", "Build the shopping list first.")); return; }
      let ok=false;
      try{ await navigator.clipboard.writeText(txt); ok=true; }
      catch(err){
        const ta=document.createElement("textarea");
        ta.value=txt; ta.style.position="fixed"; ta.style.opacity="0";
        document.body.appendChild(ta); ta.select();
        try{ ok=document.execCommand("copy"); }catch(e2){}
        document.body.removeChild(ta);
      }
      const btn=document.getElementById("copyBtn");
      btn.textContent = ok?cfg("validation.copied", "✓ Copied — paste into your notes"):cfg("validation.copyFailed", "Couldn't copy — select & copy manually");
      setTimeout(()=>{ btn.textContent="📋 Copy shopping list"; },2500);
    });

    /* ================= INIT ================= */

    state.plans[0].name = root.dataset.defaultPlanName || state.plans[0].name;
    state.plans[0].method = root.dataset.defaultMethod || state.plans[0].method;
    state.plans[0].meals = Math.min(4, Math.max(1, parseInt(root.dataset.defaultMeals, 10) || state.plans[0].meals));

    document.getElementById("days").value = root.dataset.defaultDays || document.getElementById("days").value || 7;
    document.getElementById("buffer").value = root.dataset.defaultBuffer || document.getElementById("buffer").value || "10";
    document.getElementById("rawWeights").checked = root.dataset.defaultPurchaseWeights !== "false";
    document.getElementById("costOn").checked = root.dataset.defaultCost === "true";
    document.getElementById("currency").value = root.dataset.defaultCurrency || document.getElementById("currency").value || "HK$";
    document.getElementById("curWrap").hidden = !document.getElementById("costOn").checked;

    const initialDays = parseInt(document.getElementById("days").value, 10);
    document.querySelectorAll(".daysChip").forEach(chip=>{
      chip.classList.toggle("is-selected", Number(chip.dataset.d) === initialDays);
      chip.setAttribute("aria-pressed", Number(chip.dataset.d) === initialDays ? "true" : "false");
    });

    renderPlans();
    // Auto-import a recipe handed over from the Homemade Food Calculator via #r= in the URL
    (function(){
      if(!window.location.hash || !window.location.hash.startsWith("#r=")) return;
      let txt="";
      try{ txt = decodeURIComponent(window.location.hash.slice(3)); }catch(e){ return; }
      const res = parseRecipeText(txt, state.plans[0]);
      if(!res.ok) return;
      renderPlans();
      const p = state.plans[0];
      const box = plansEl.querySelector(`.pasteBox[data-p="${p.id}"]`);
      const det = plansEl.querySelector(`.plan[data-plan="${p.id}"] details.importer`);
      const st  = plansEl.querySelector(`.stat[data-p="${p.id}"]`);
      if(box) box.value = txt;
      if(det) det.open = true;
      if(st){ st.textContent = "✓ Recipe received from the Homemade Food Calculator — " + res.msg.replace(/^✓\s*/,""); st.className = "shopping-prep-calculator__import-status stat is-success"; }
      calculate(false);
    })();

  }
}

if (!customElements.get('s-shopping-prep-calculator')) {
  customElements.define('s-shopping-prep-calculator', SShoppingPrepCalculator);
}
})();
